// MyTestimonies.jsx
// Route: /my-testimonies
// Linked from Profile page via the "Testimonies" row.

import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router";
import {
  ChevronLeft, Plus, Eye, Heart, CheckCircle, Clock,
  MoreHorizontal, Edit3, Trash2, Share2, XCircle,
} from "lucide-react";
import BottomNav from "../../Sections/BottomNav/BottomNav";
import api from "../../services/axiosConfig";
import "./styles.css";

const TAG_STYLES = {
  "Healing Streams":          { bg: "#dcccf4", color: "#5a3d8a", accent: "#9b7fd4" },
  "Partnership":              { bg: "#f6d7be", color: "#8a5a2a", accent: "#e0945a" },
  "Healing to the Nations":   { bg: "#d4e8d4", color: "#2a6b3a", accent: "#5aaa6a" },
  "Magazines":                { bg: "#e8d5b0", color: "#6b4a1a", accent: "#c9a96e" },
  "Prayer Clouds":            { bg: "#e0f0f8", color: "#1a5a7a", accent: "#5ba2c4" },
  "Crusades":                 { bg: "#ffe4d4", color: "#8a3a1a", accent: "#e46f48" },
  "Pray with Me":             { bg: "#f3d8e4", color: "#8a3a5a", accent: "#c45b8a" },
  "Heralds":                  { bg: "#fef3c7", color: "#92610a", accent: "#e4b448" },
};

const getTagStyle = (cat) => TAG_STYLES[cat] || { bg: "#e8d5b0", color: "#6b4a1a", accent: "#c9a96e" };

// Filter options
const FILTERS = [
  { id: "all",      label: "All"      },
  { id: "approved", label: "Published" },
  { id: "pending",  label: "Pending"  },
];

// ─── Format helpers ────────────────────────────────────────────────────────────
function fmtNum(n) {
  if (n === null || n === undefined) return null;
  return n >= 1000 ? `${(n / 1000).toFixed(1)}K` : String(n);
}

// ─── Context menu ─────────────────────────────────────────────────────────────
function CardMenu({ testimony, onDelete, onShare }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div className="mt-menu-wrap" ref={ref}>
      <button
        className="mt-menu-btn"
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        aria-label="More options"
        aria-expanded={open}
      >
        <MoreHorizontal size={16} />
      </button>

      {open && (
        <div className="mt-dropdown" role="menu">
          <button
            className="mt-dropdown-item"
            role="menuitem"
            onClick={(e) => {
              e.stopPropagation();
              setOpen(false);
              onShare(testimony);
            }}
          >
            <Share2 size={14} /> Share
          </button>

          <button
            className="mt-dropdown-item"
            role="menuitem"
            onClick={(e) => { e.stopPropagation(); setOpen(false); navigate(`/upload?edit=${testimony.id}`); }}
          >
            <Edit3 size={14} /> Edit
          </button>

          <div className="mt-dropdown-divider" />

          <button
            className="mt-dropdown-item danger"
            role="menuitem"
            onClick={(e) => {
              e.stopPropagation();
              setOpen(false);
              onDelete(testimony.id);
            }}
          >
            <Trash2 size={14} /> Delete
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Single testimony card ─────────────────────────────────────────────────────
function TestimonyCard({ testimony, onDelete, onShare }) {
  const navigate = useNavigate();
  const isClickable = testimony.status === "approved";

  const handleClick = () => {
    if (isClickable) navigate(`/testimony/${testimony.id}`);
  };

  return (
    <article
      className={`mt-card${isClickable ? " clickable" : ""}`}
      onClick={handleClick}
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={(e) => e.key === "Enter" && handleClick()}
      aria-label={testimony.title}
    >
      {/* colour-coded accent bar */}
      <div className="mt-card-accent" style={{ background: testimony.accentColor }} />

      <div className="mt-card-body">
        {/* top row: tag + status + menu */}
        <div className="mt-card-top">
          <div className="mt-card-top-left">
            <span
              className="mt-tag"
              style={{ background: testimony.tagBg, color: testimony.tagColor }}
            >
              {testimony.tag}
            </span>

            <span className={`mt-status ${testimony.status}`}>
              {testimony.status === "approved" && <><CheckCircle size={11} /> Published</>}
              {testimony.status === "pending"  && <><Clock size={11} /> Pending Admin Review</>}
              {testimony.status === "rejected" && <><XCircle size={11} /> Rejected</>}
            </span>
          </div>

          <CardMenu
            testimony={testimony}
            onDelete={onDelete}
            onShare={onShare}
          />
        </div>

        {/* title */}
        <h2 className="mt-card-title">{testimony.title}</h2>

        {/* footer */}
        <div className="mt-card-footer">
          <span className="mt-card-date">{testimony.date}</span>

          {testimony.status === "approved" && (
            <div className="mt-card-stats">
              <span><Eye size={11} /> {fmtNum(testimony.views)}</span>
              <span><Heart size={11} /> {fmtNum(testimony.likes)}</span>
            </div>
          )}

          {testimony.status === "pending" && (
            <span className="mt-pending-note">Awaiting Admin Approval</span>
          )}
        </div>
      </div>
    </article>
  );
}

// ─── MyTestimonies page ────────────────────────────────────────────────────────
export default function MyTestimonies() {
  const navigate  = useNavigate();
  const [items,   setItems]   = useState([]);
  const [filter,  setFilter]  = useState("all");
  const [scrolled, setScrolled] = useState(false);
  const [loading, setLoading] = useState(true);

  // Fetch user testimonies on mount
  useEffect(() => {
    fetchMyTestimonies();
  }, []);

  const fetchMyTestimonies = async () => {
    try {
      setLoading(true);
      const res = await api.get("/api/users/me/testimonies");
      // Map API DTO to expected frontend schema
      const mapped = (res.data.content || []).map((t) => {
        const catName = t.category?.name || "Healing Streams";
        const style = getTagStyle(catName);
        return {
          id: t.id,
          title: t.title,
          status: t.status.toLowerCase(),
          tag: catName,
          tagBg: style.bg,
          tagColor: style.color,
          accentColor: style.accent,
          views: t.viewCount,
          likes: t.likeCount,
          date: t.createdAt ? new Date(t.createdAt).toLocaleDateString() : "Just now",
        };
      });
      setItems(mapped);
    } catch (err) {
      console.error("Error fetching my testimonies:", err);
    } finally {
      setLoading(false);
    }
  };

  // Sticky header shadow on scroll
  useEffect(() => {
    const el = document.querySelector(".mt-page");
    if (!el) return;
    const handler = () => setScrolled(el.scrollTop > 8);
    el.addEventListener("scroll", handler);
    return () => el.removeEventListener("scroll", handler);
  }, []);

  // Delete a testimony
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this testimony? This can't be undone.")) return;
    try {
      await api.delete(`/api/testimonies/${id}`);
      setItems((prev) => prev.filter((t) => t.id !== id));
    } catch (err) {
      alert("Failed to delete testimony: " + (err.response?.data?.message || err.message));
    }
  };

  // Share a testimony
  const handleShare = (testimony) => {
    const url  = `${window.location.origin}/testimony/${testimony.id}`;
    const text = `"${testimony.title}" — read on My Miracle Story`;
    if (navigator.share) {
      navigator.share({ title: testimony.title, text, url }).catch(() => {});
    } else {
      navigator.clipboard?.writeText(url);
    }
  };

  // Derived: filtered list + stats
  const filtered = filter === "all" ? items : items.filter((t) => t.status === filter);
  const totalViews  = items.filter((t) => t.views).reduce((s, t) => s + t.views, 0);
  const totalLikes  = items.filter((t) => t.likes).reduce((s, t) => s + t.likes, 0);
  const approvedCount = items.filter((t) => t.status === "approved").length;
  const pendingCount  = items.filter((t) => t.status === "pending").length;

  return (
    <div className="mt-page">

      {/* ── Top bar ── */}
      <header className={`mt-topbar${scrolled ? " scrolled" : ""}`}>
        <div className="mt-topbar-left">
          <button className="mt-back-btn" onClick={() => navigate(-1)} aria-label="Back">
            <ChevronLeft size={20} />
          </button>
          <h1>My Testimonies</h1>
        </div>
      </header>

      {/* ── Summary strip ── */}
      <div className="mt-summary">
        <div className="mt-stat">
          <span className="mt-stat-value">{items.length}</span>
          <span className="mt-stat-label">Total</span>
        </div>
        <div className="mt-stat">
          <span className="mt-stat-value">{approvedCount}</span>
          <span className="mt-stat-label">Published</span>
        </div>
        <div className="mt-stat">
          <span className="mt-stat-value">{fmtNum(totalViews)}</span>
          <span className="mt-stat-label">Views</span>
        </div>
        <div className="mt-stat">
          <span className="mt-stat-value">{fmtNum(totalLikes)}</span>
          <span className="mt-stat-label">Likes</span>
        </div>
      </div>

      {/* ── Filter tabs ── */}
      <div className="mt-filters">
        {FILTERS.map((f) => {
          const count =
            f.id === "all"      ? items.length :
            f.id === "approved" ? approvedCount :
            pendingCount;
          return (
            <button
              key={f.id}
              className={`mt-filter-btn${filter === f.id ? " active" : ""}`}
              onClick={() => setFilter(f.id)}
            >
              {f.label}
              <span className="mt-filter-count">{count}</span>
            </button>
          );
        })}
      </div>

      {/* ── Card list ── */}
      <div className="mt-list">
        {loading ? (
          <div className="mt-empty">
            <h3>Loading...</h3>
          </div>
        ) : filtered.length === 0 ? (
          <div className="mt-empty">
            <div className="mt-empty-icon">✦</div>
            <h3>Nothing here yet</h3>
            <p>
              {filter === "pending"
                ? "You have no testimonies pending review."
                : "You haven't shared a testimony yet. God has done something — tell it."}
            </p>
          </div>
        ) : (
          filtered.map((t, i) => (
            <div key={t.id} style={{ animationDelay: `${i * 0.06}s` }}>
              <TestimonyCard
                testimony={t}
                onDelete={handleDelete}
                onShare={handleShare}
              />
            </div>
          ))
        )}
      </div>

      {/* ── FAB ── */}
      <button
        className="mt-fab"
        onClick={() => navigate("/upload")}
        aria-label="Share a new testimony"
      >
        <Plus size={16} />
        Share New
      </button>

      <BottomNav />
    </div>
  );
}